import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AxiosError, AxiosResponse } from 'axios';
import {
  Button,
  Tag,
  Badge,
  message,
  Skeleton,
  Typography
} from 'antd';
import {
  CaretRightOutlined,
  FormOutlined,
  DeleteOutlined,
  SettingOutlined,
  CheckCircleFilled,
  LikeTwoTone,
  DislikeTwoTone,
  LikeFilled,
  DislikeFilled
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import CodeMirror from '@uiw/react-codemirror';
import { indentUnit, LanguageSupport } from '@codemirror/language'
import { python } from '@codemirror/lang-python';
import { sql } from '@codemirror/lang-sql';
import { vim } from '@replit/codemirror-vim';
import {
  getProblem,
  getProblemSets,
  submitProblem,
  likeProblem,
  dislikeProblem,
  ProblemNestedResponse,
  ProblemSetNestedResponse,
  SubmitProblemResponse
} from '../../api/problems';
import { formatString } from '../../utils/misc';
import { AuthContext, AuthContextState } from '../../context/AuthContext';
import MainLayout from '../../components/MainLayout';
import ProblemResultsModal from '../../components/ProblemResultsModal';
import ProblemTestcaseModal from '../../components/ProblemTestcaseModal';
import ProblemTestcaseProgress from '../../components/ProblemTestcaseProgress';
import {
  DIFFICULTY_MAP,
  LOGIN_ROUTE,
  SETTINGS_ROUTE,
  NOT_FOUND_ROUTE,
  APPEARANCE_SETTINGS_EDITOR_KEY_BINDINGS_LOCALSTORAGE_KEY,
  APPEARANCE_SETTINGS_EDITOR_THEME_LOCALSTORAGE_KEY,
  EDITOR_CACHE_LOCALSTORAGE_KEY
} from '../../config/constants';
import styles from './Problem.module.css';
import { ThemeContext, ThemeContextState } from '../../context/ThemeContext';

export interface ProblemProps {};

// modals available
enum Modals {
  // shows submission results after submission
  Results,
  // allows specification of custom testcases
  CustomTestcase,
  // indicates when no modal is displayed
  None
};

// mapping of problem type to codemirror language extension function
const ProblemTypeToCodeMirrorLang: Record<string, () => LanguageSupport> = {
  'P': python,
  'S': sql,
};

// page for problem
// shows problem description
// includes editor for coding
// allows user to submit code
const Problem = (props: ProblemProps): JSX.Element | null => {
  // problem info
  const [problem, setProblem] = useState<ProblemNestedResponse | null>(null);
  // fetched list of problem sets
  const [problemSets, setProblemSets] = useState<ProblemSetNestedResponse[]>([]);
  // currently displayed modal
  const [showModal, setShowModal] = useState<Modals>(Modals.None);
  // editor submission code
  const [submissionCode, setSubmissionCode] = useState<string>('');
  // results of submission
  const [submissionResults, setSubmissionResults] = useState<SubmitProblemResponse | null>(null);
  // whether or not current submission results are from custom testcase
  const [isCustomTestcase, setIsCustomTestcase] = useState<boolean>(false);
  // whether or not submissions are disabled
  const [submissionDisabled, setSubmissionDisabled] = useState<boolean>(false);
  // whether or not liking/disliking is disabled
  const [likingDisabled, setLikingDisabled] = useState<boolean>(false);
  // screen size media query
  const [matches, setMatches] = useState(window.matchMedia('(min-width: 767px)').matches);
  // alert messages
  const [alertMessageApi, alertMessageContextHolder] = message.useMessage();
  // ref to avoid fetching problems data multiple times on multiple renders
  const fetchProblemsDataCalled = useRef<boolean>(false);
  // get refresh request function from auth context
  const { authState, refreshRetryRequest, me } = useContext(AuthContext) as AuthContextState;
  // get current theme from theme context
  const { theme } = useContext(ThemeContext) as ThemeContextState;
  // URL parameters
  const urlParams = useParams();
  // route navigator
  const navigate = useNavigate();
  // vim extension
  const vimExtension = vim();

  // get problem id or slug from URL parameters
  var problemIDOrSlug: number | string = Number(urlParams.key);
  if (isNaN(problemIDOrSlug)) {
    problemIDOrSlug = (urlParams.key as string);
  }

  // get problem data
  const fetchProblem = useCallback(async () => {
    if (!urlParams.key) {
      navigate(NOT_FOUND_ROUTE);
    }

    try {
      const response: AxiosResponse = await refreshRetryRequest(
        async (accessToken: string | null) => await getProblem(
          accessToken,
          problemIDOrSlug,
        )
      );

      const problemResponse = response.data as ProblemNestedResponse;
      const starterCode = localStorage.getItem(formatString(EDITOR_CACHE_LOCALSTORAGE_KEY, { userUUID: me?.uuid, problemID: problemResponse.id }));

      const isProblemFetched = problem != null;
      setProblem(problemResponse);
      setSubmissionCode(starterCode ?? problemResponse.starter_code);
      if (starterCode && !isProblemFetched) {
        alertMessageApi.info('Your progress from a previous session has been restored');
      }
    } catch (error: any) {
      if ((error as AxiosError).response?.status === 404) {
        navigate(NOT_FOUND_ROUTE);
      }

      // try fetching problem without access token if previous calls fail
      try {
        const response: AxiosResponse = await getProblem(null, problemIDOrSlug);
        setProblem(response.data as ProblemNestedResponse);
      } catch(error: any) {
        if ((error as AxiosError).response?.status === 404) {
          navigate(NOT_FOUND_ROUTE);
        }
      }
    }
  }, [refreshRetryRequest, problemIDOrSlug, navigate, urlParams.key, alertMessageApi, problem, me]);

  // set document title
  useEffect(() => {
    document.title = (problem ? problem.title : 'Problem') + ' | Sigmoid Academy';
  }, [problem]);

  // check screen size and update state
  useEffect(() => {
    window.matchMedia(
      '(min-width: 767px)'
    ).addEventListener('change', e => setMatches(e.matches));
  }, []);

  // fetch problem info and list of solved problems on render
  useEffect(() => {
    if (fetchProblemsDataCalled.current) {
      return;
    }

    fetchProblemsDataCalled.current = true;

    // get current problem info
    fetchProblem();

    const onSuccess = (response: AxiosResponse) => setProblemSets(response.data as ProblemSetNestedResponse[]);
    // get problem sets
    refreshRetryRequest(
      getProblemSets
    ).then(
      onSuccess
    ).catch((error: any) => {
      // get problem sets without access token if previous call fails
      getProblemSets(
        null
      ).then(
        onSuccess
      ).catch((error: any) => console.log('Error in getting problem sets'));
    });
  }, [fetchProblem, refreshRetryRequest, problemIDOrSlug, navigate]);

  // handle editor code change
  const handleEditorChange = (value: string, viewUpdate: object) => {
    setSubmissionCode(value);
  };

  // handle reset editor code
  const handleEditorReset = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();

    if (!problem) {
      return;
    }

    setSubmissionCode(problem.starter_code);
    localStorage.removeItem(formatString(EDITOR_CACHE_LOCALSTORAGE_KEY, { userUUID: me?.uuid, problemID: problem.id }));

    alertMessageApi.success('Your progress has been reset');
  };

  // handle cache editor code
  const handleEditorCache = (e: React.FocusEvent<HTMLElement>) => {
    e.preventDefault();

    if (!problem || !me) {
      return;
    }

    localStorage.setItem(formatString(EDITOR_CACHE_LOCALSTORAGE_KEY, { userUUID: me.uuid, problemID: problem.id }), submissionCode);
  };

  // handle code submission
  const handleSubmission = (e: React.MouseEvent<HTMLElement>): void => {
    e.preventDefault();

    if (submissionDisabled) {
      return;
    }

    if (!problem) {
      return;
    }

    setSubmissionDisabled(true);

    // submit code as solution
    refreshRetryRequest(
      async (accessToken: string | null) => await submitProblem(
        accessToken,
        problem.slug ?? problem.id,
        submissionCode,
      )
    ).then((response: AxiosResponse) => {
        const submitResponse = (response.data as SubmitProblemResponse);
        if (submitResponse.run_info.signal?.toUpperCase() === 'SIGKILL') {
          alertMessageApi.warning('Time limit exceeded');
          return;
        }

        if (submitResponse.run_info.code !== 0) {
          alertMessageApi.error('Unknown error occurred, please try again');
          return;
        }

        // refetch solved problems if this problem is solved successfully
        if (submitResponse.run_info.output.outcome) {
          fetchProblem();
        }

        // set submission results and testcase progress bar
        setSubmissionResults({...submitResponse});
        setIsCustomTestcase(false);
        setShowModal(Modals.Results);
      }
    ).catch((error: any) => alertMessageApi.error('Unknown error occurred, please try again')
    ).finally(() => setSubmissionDisabled(false));
  };

  // handle code submission with custom testcase
  const handleCustomTestcase = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    setShowModal(Modals.CustomTestcase);
  }

  // handle custom testcase modal run
  const handleCustomTestcaseModalRun = (e: React.MouseEvent<HTMLElement>, testcaseArgs: any[]) => {
    if (submissionDisabled) {
      return;
    }

    if (!problem) {
      return;
    }

    setSubmissionDisabled(true);

    // submit code as solution with custom testcase
    refreshRetryRequest(
      async (accessToken: string | null) => await submitProblem(
        accessToken,
        problem.id,
        submissionCode,
        testcaseArgs
      )
    ).then(
      (response: AxiosResponse) => {
        // set submission results
        const submitResponse = response.data as SubmitProblemResponse;
        if (submitResponse.run_info.signal?.toUpperCase() === 'SIGKILL') {
          alertMessageApi.warning('Time limit exceeded');
          return;
        }

        if (submitResponse.run_info.code !== 0) {
          alertMessageApi.error('Unknown error occurred, please try again');
          return;
        }

        setSubmissionResults({...submitResponse});
        setIsCustomTestcase(true);
        setShowModal(Modals.Results);
      }
    ).catch((error: any) => console.log('Error in submitting problem request')
    ).finally(() => setSubmissionDisabled(false));
  };

  // redirect to editor settings page
  const handleRedirectToEditorSettings = (e: React.MouseEvent<HTMLElement>): void => {
    e.preventDefault();

    // set up editor settings url
    const params = new URLSearchParams();
    params.append('tab', 'appearance');

    navigate(SETTINGS_ROUTE + '?' + params.toString());
  };

  // redirect to login page
  const handleRedirectToLogin = (e: React.MouseEvent<HTMLElement>): void => {
    e.preventDefault();

    // set up login url
    const params = new URLSearchParams();
    params.append('next', window.location.pathname);

    navigate(LOGIN_ROUTE + '?' + params.toString());
  };

  // handle like problem
  const handleLike = (e: React.MouseEvent<HTMLElement>): void => {
    e.preventDefault();

    if (likingDisabled) {
      return;
    }

    if (!problem) {
      return;
    }

    setLikingDisabled(true);

    // like problem
    refreshRetryRequest(
      async (accessToken: string | null) => await likeProblem(accessToken, problem.id)
    ).then((response: AxiosResponse) => fetchProblem()
    ).catch((error: any) => alertMessageApi.error('Unknown error occurred, please try again')
    ).finally(() => setLikingDisabled(false));
  };

  // handle dislike problem
  const handleDislike = (e: React.MouseEvent<HTMLElement>): void => {
    e.preventDefault();

    if (likingDisabled) {
      return;
    }

    if (!problem) {
      return;
    }

    setLikingDisabled(true);

    // dislike problem
    refreshRetryRequest(
      async (accessToken: string | null) => await dislikeProblem(accessToken, problem.id)
    ).then((response: AxiosResponse) => fetchProblem()
    ).catch((error: any) => alertMessageApi.error('Unknown error occurred, please try again')
    ).finally(() => setLikingDisabled(false));
  };

  // get editor key bindings settings
  const editorKeyBindings = localStorage.getItem(APPEARANCE_SETTINGS_EDITOR_KEY_BINDINGS_LOCALSTORAGE_KEY) || 'standard';
  // get editor theme settings
  const editorTheme = localStorage.getItem(APPEARANCE_SETTINGS_EDITOR_THEME_LOCALSTORAGE_KEY) || 'dark';

  return (
    <div>
      <MainLayout selectedKey='problem-sets'>
        {alertMessageContextHolder}
        {problem
        ? (
          <div className={styles.mainContainer}>
            <ProblemResultsModal
              isOpen={showModal === Modals.Results}
              isTestcase={isCustomTestcase}
              outcome={submissionResults ? submissionResults.run_info.output.outcome : false}
              passedTestcases={submissionResults ? submissionResults.run_info.output.passed_testcases : 0}
              totalTestcases={submissionResults ? submissionResults.run_info.output.total_testcases : 0}
              failingTestcase={submissionResults ? submissionResults.run_info.output.failing_testcase : ''}
              expectedOutput={submissionResults ? submissionResults.run_info.output.expected : ''}
              computedOutput={submissionResults ? submissionResults.run_info.output.computed : ''}
              stdout={submissionResults ? submissionResults.run_info.output.stdout : ''}
              stderr={submissionResults ? submissionResults.run_info.output.stderr : ''}
              efficiencyScore={submissionResults ? submissionResults.run_info.output.efficiency_score : null}
              problem={problem}
              problemSets={problemSets}
              handleClose={() => setShowModal(Modals.None)}
            />
            <ProblemTestcaseModal
                isOpen={showModal === Modals.CustomTestcase}
                isLoading={submissionDisabled}
                handleRun={handleCustomTestcaseModalRun}
                handleClose={() => setShowModal(Modals.None)}
              />
            <div className={styles.actionsContainer}>
              <div className={styles.actionsContainerLeft}>
                {submissionResults && !isCustomTestcase &&
                  <ProblemTestcaseProgress
                    passedTestcases={submissionResults.run_info.output.passed_testcases}
                    totalTestcases={submissionResults.run_info.output.total_testcases}
                  />
                }
              </div>
              <div className={styles.actionsContainerRight}>
                {authState && matches && <Button
                  onClick={handleRedirectToEditorSettings}
                  type={'primary'}
                  className={'secondaryButton'}
                >
                  <SettingOutlined />
                  Settings
                </Button>}
                {authState && matches && <Button
                  onClick={handleEditorReset}
                  type={'primary'}
                  className={'secondaryButton'}
                >
                  <DeleteOutlined />
                  Reset
                </Button>}
                {authState && problem.child_problem?.allow_testcases && matches && <Button
                  onClick={handleCustomTestcase}
                  type={'primary'}
                  className={'secondaryButton'}
                >
                  <FormOutlined />
                  Testcase
                </Button>}
                {authState && <Button
                  onClick={handleSubmission}
                  type={'primary'}
                  loading={submissionDisabled}
                  className={'mainButton'}
                >
                  <CaretRightOutlined />
                  Submit
                </Button>}
                {!authState &&
                  <Button
                    type={'primary'}
                    className={'mainButton'}
                    onClick={handleRedirectToLogin}
                  >
                    Login to Start Coding
                  </Button>
                }
              </div>
            </div>
            <div className={styles.columnsContainer}>
              <div className={styles.descriptionContainer}>
                <div className={styles.descriptionHeader}>
                  <div className={styles.descriptionProblemSetContainer}>
                    {problem.problemset.icon !== null &&
                      <img
                        className={styles.problemSetIcon}
                        alt={`${problem.problemset.title} Icon`}
                        src={problem.problemset.icon}
                      />
                    }
                    <Typography.Text className={styles.problemSetTag}>
                      {problem.problemset.title}
                    </Typography.Text>
                  </div>
                  <div className={styles.descriptionLikesContainer}>
                    <div className={styles.descriptionLikePairContainer}>
                      <div
                        onClick={authState ? handleLike : handleRedirectToLogin}
                        className={`${styles.descriptionLikeContainer} ${styles.descriptionLikeClickable}`}
                      >
                        {problem.liked ?
                        <LikeFilled style={{ color: '#9290F8'}} /> :
                        <LikeTwoTone twoToneColor='#9290F8' />}
                      </div>
                      <div className={styles.descriptionLikeCountContainer}>
                        <Typography.Text className={styles.descriptionLikeCount}>
                          {Intl.NumberFormat('en-US', {
                            notation: 'compact',
                            maximumFractionDigits: 1
                          }).format(problem.likes)}
                        </Typography.Text>
                      </div>
                    </div>
                    <div className={styles.descriptionLikePairContainer}>
                      <div
                        onClick={authState ? handleDislike : handleRedirectToLogin}
                        className={`${styles.descriptionLikeContainer} ${styles.descriptionDislikeClickable}`}
                      >
                        {problem.disliked ?
                        <DislikeFilled style={{ color: '#9290F8'}} /> :
                        <DislikeTwoTone twoToneColor='#9290F8' />}
                      </div>
                      <div className={styles.descriptionLikeCountContainer}>
                        <Typography.Text className={styles.descriptionLikeCount}>
                          {Intl.NumberFormat('en-US', {
                            notation: 'compact',
                            maximumFractionDigits: 1
                          }).format(problem.dislikes)}
                        </Typography.Text>
                      </div>
                    </div>
                  </div>
                  <div className={styles.descriptionTagsContainer}>
                    <Tag className={DIFFICULTY_MAP[problem.difficulty].tagClass}>
                      {DIFFICULTY_MAP[problem.difficulty].name}
                    </Tag>
                  </div>
                </div>
                <ReactMarkdown
                  children={
                    problem.description.replaceAll(
                      '{theme}',
                      theme
                    ).replaceAll(
                      '{color}',
                      theme === 'dark' ? 'white' : 'black'
                    )
                  }
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  className={styles.markdown}
                />
                {problem.contributors.length > 0 &&
                  <div className={styles.problemContributorsContainer}>
                    <Typography.Text className={styles.problemContributorText}>
                      Supporting contributors: {problem.contributors.join(', ')}
                    </Typography.Text>
                  </div>
                }
              </div>
              <div
                className={`${styles.codemirrorMainContainer} ${!authState ? styles.codemirrorMainContainerBlur: ''}`}
                onClick={authState ? () => {} : handleRedirectToLogin}
              >
                <div className={`${!authState ? styles.codemirrorHoverText: styles.codemirrorHoverTextHide}`}>
                  Login to Start Coding
                </div>
                <div className={styles.codemirrorEditorContainer}>
                  <Badge.Ribbon
                    text={<CheckCircleFilled className={'badgeRibbonCheckIcon'}/>}
                    placement='end'
                    className={problem.is_solved ? 'badgeRibbonShowDark' : 'badgeRibbonHide'}
                  >
                    <CodeMirror
                      onBlur={handleEditorCache}
                      onChange={handleEditorChange}
                      value={submissionCode}
                      extensions={[
                        ProblemTypeToCodeMirrorLang[problem.type](),
                        indentUnit.of('    '),
                        ...(
                          editorKeyBindings === 'vim'
                          ? [vimExtension] : []
                        )
                      ]}
                      theme={editorTheme as 'light' | 'dark'}
                      indentWithTab={true}
                      width='100%'
                      height={`calc(100vh - ${matches ? '200px' : '110px'})`}
                      editable={authState}
                    />
                  </Badge.Ribbon>
                </div>
              </div>
            </div>
          </div>
        )
        :
        <div className={styles.skeletonContainer}>
          <div className={styles.skeletonLeftContainer}>
            <Skeleton active={true} title={true} paragraph={{rows: 5}} />
            <Skeleton active={true} title={false} paragraph={{rows: 4}} />
          </div>
          <div className={styles.skeletonRightContainer}>
            <Skeleton active={true} title={true} paragraph={{rows: 2}} />
            <Skeleton active={true} title={false} paragraph={{rows: 3}} />
            <Skeleton active={true} title={true} paragraph={{rows: 1}} />
            <Skeleton active={true} title={false} paragraph={{rows: 2}} />
          </div>
        </div>}
      </MainLayout>
    </div>
  );
};

export default Problem;
